/**
 * City day/night palette (Onda "Cidade Noturna") — CLIENT-ONLY, ZERO wire.
 *
 * The walkable ports (capital + villages) used to be lit by one static golden-hour look while the SEA
 * ran a full day→night cycle. This module gives the city the SAME cycle: it maps the time-of-day phase
 * (the very same wall-clock cadence the sea sky uses — see {@link cityPhase}) to a coherent palette for
 * the scene's GLOBAL lights + sky/fog, plus two cheap "night truths": how brightly building windows glow
 * and how much the torches take over.
 *
 * It is a PURE module (numbers only, no three.js): `cityPaletteAt(phase)` is a deterministic function of
 * the phase, unit-testable without a renderer. `portscene.ts` converts the packed 0xRRGGBB colours to
 * THREE.Color and drives the lights each frame; `housescene.ts` reads `nightFactor` to cool-tint the cabin.
 *
 * Design goals from the art brief (a walled town at night — cool-blue ambient, warm lit windows, torches
 * dominating): midnight is MOONLIT BLUE, never pitch black; dawn/sunset are golden; noon is a clear neutral
 * day that reproduces the port's original daylight balance. Night is the same ~19% slice the sea uses, so
 * the two never disagree about what time it is.
 */
import { dayNightPhase } from '@piratas/protocol';

/** A resolved city palette for one instant. Colours are packed 0xRRGGBB; intensities are absolute. */
export interface CityPalette {
  /** Scene background / sky tint (the horizon the fog fades into). */
  sky: number;
  /** Fog colour (kept close to `sky` so distance dissolves into the same haze). */
  fog: number;
  /** AmbientLight colour + intensity — the cool-blue floor that keeps a night city readable (never black). */
  ambient: number;
  ambientI: number;
  /** HemisphereLight sky/ground colours + intensity. */
  hemiSky: number;
  hemiGround: number;
  hemiI: number;
  /** DirectionalLight ("sun" by day, "moon" by night) colour + intensity. */
  sun: number;
  sunI: number;
  /** Window emissive strength 0..1 (0 = unlit daylight window, 1 = fully lit warm pane at night). */
  windowGlow: number;
  /** Torch flame + light multiplier (1 by day → the braziers quietly take over the darker it gets). */
  torchBoost: number;
  /** 0 day … 1 deep night — a single "nightness" scalar (drives the house cool-tint + any night-only cue). */
  nightFactor: number;
}

interface CityKey extends CityPalette { p: number; }

const k = (
  p: number, sky: number, fog: number, ambient: number, ambientI: number,
  hemiSky: number, hemiGround: number, hemiI: number, sun: number, sunI: number,
  windowGlow: number, torchBoost: number, nightFactor: number,
): CityKey => ({ p, sky, fog, ambient, ambientI, hemiSky, hemiGround, hemiI, sun, sunI, windowGlow, torchBoost, nightFactor });

/**
 * City keyframes at the SAME phase anchors as the sea's SKY_KEYS (0=midnight · 0.5=noon · 0.75-ish dusk),
 * so the port and the sea read the same hour. The noon key reproduces the capital's shipped daylight
 * (bg 0x9cc3e4 / hemi 0xdfeeff·0x4a3a28 @1.0 / sun 0xffe6bc @1.5 / ambient 0x6b5a44 @0.32) so a bright
 * midday port looks exactly as it always did. Night keeps a COOL-BLUE ambient floor (moonlit, not black).
 */
const CITY_KEYS: CityKey[] = [
  // p     sky       fog       ambient   aI     hemiSky   hemiGrnd  hI     sun       sI     win   torch  night
  k(0.00, 0x18244a, 0x22315a, 0x2b3a60, 0.55, 0x46567f, 0x161d30, 0.75, 0x9fb4e6, 0.35, 1.00, 1.60, 1.00), // midnight — moonlit blue, NOT black
  k(0.12, 0x6a6a9c, 0xb890a0, 0x4a4258, 0.44, 0x93a4cc, 0x40352f, 0.90, 0xffb070, 0.85, 0.45, 1.20, 0.35), // dawn — pink/gold glow
  k(0.22, 0xa9c8e6, 0xc4dcee, 0x5c5240, 0.34, 0xcfe6ff, 0x40342a, 1.00, 0xffe6c4, 1.30, 0.05, 1.00, 0.02), // morning
  k(0.50, 0x9cc3e4, 0xbcd6e8, 0x6b5a44, 0.32, 0xdfeeff, 0x4a3a28, 1.00, 0xffe6bc, 1.50, 0.00, 1.00, 0.00), // noon — shipped daylight
  k(0.72, 0x9fb2cc, 0xd7bd94, 0x5a4a3a, 0.36, 0xdcc6ae, 0x3a3020, 0.94, 0xffd9a0, 1.15, 0.18, 1.08, 0.08), // golden hour
  k(0.84, 0x4a3a72, 0xb06e78, 0x4a3a54, 0.44, 0xcf9f8f, 0x2e2632, 0.80, 0xff7a4a, 0.70, 0.62, 1.28, 0.42), // sunset — gold → purple
  k(0.93, 0x28285a, 0x363662, 0x30365c, 0.50, 0x62628e, 0x20223c, 0.74, 0x9a8ac8, 0.42, 0.95, 1.50, 0.88), // twilight — deep-blue, windows on
  k(1.00, 0x18244a, 0x22315a, 0x2b3a60, 0.55, 0x46567f, 0x161d30, 0.75, 0x9fb4e6, 0.35, 1.00, 1.60, 1.00), // → midnight (wrap)
];

/** City time-of-day phase 0..1 — the SAME wall-clock cycle the sea sky uses (never the sim clock). Kept a
 *  one-line re-export of the protocol source so the port can NEVER drift from the sea: entering a port at
 *  "22:00 game time" gives a night city, and stepping back out lands on the sea at the same hour. */
export function cityPhase(epochMs: number = Date.now()): number { return dayNightPhase(epochMs); }

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Lerp two packed 0xRRGGBB colours per-channel (sRGB byte space — the consumer re-applies colour management). */
function lerpHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(lerp(ar, br, t)) & 0xff;
  const g = Math.round(lerp(ag, bg, t)) & 0xff;
  const bl = Math.round(lerp(ab, bb, t)) & 0xff;
  return (r << 16) | (g << 8) | bl;
}

/**
 * Resolve the city palette at `phase` (0..1, wraps). Fills `out` if provided (allocation-free for the
 * per-frame render path); otherwise returns a fresh object. Linear interpolation between the two bracketing
 * keyframes — the same scheme the sea sky uses.
 */
export function cityPaletteAt(phase: number, out?: CityPalette): CityPalette {
  const p = ((phase % 1) + 1) % 1;
  let a = CITY_KEYS[0]!, b = CITY_KEYS[CITY_KEYS.length - 1]!;
  for (let i = 0; i < CITY_KEYS.length - 1; i++) {
    if (p >= CITY_KEYS[i]!.p && p <= CITY_KEYS[i + 1]!.p) { a = CITY_KEYS[i]!; b = CITY_KEYS[i + 1]!; break; }
  }
  const t = (p - a.p) / Math.max(1e-6, b.p - a.p);
  const o = out ?? ({} as CityPalette);
  o.sky = lerpHex(a.sky, b.sky, t);
  o.fog = lerpHex(a.fog, b.fog, t);
  o.ambient = lerpHex(a.ambient, b.ambient, t);
  o.ambientI = lerp(a.ambientI, b.ambientI, t);
  o.hemiSky = lerpHex(a.hemiSky, b.hemiSky, t);
  o.hemiGround = lerpHex(a.hemiGround, b.hemiGround, t);
  o.hemiI = lerp(a.hemiI, b.hemiI, t);
  o.sun = lerpHex(a.sun, b.sun, t);
  o.sunI = lerp(a.sunI, b.sunI, t);
  o.windowGlow = lerp(a.windowGlow, b.windowGlow, t);
  o.torchBoost = lerp(a.torchBoost, b.torchBoost, t);
  o.nightFactor = lerp(a.nightFactor, b.nightFactor, t);
  return o;
}
